import React, { Fragment } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import Pages from '../pages';
import {
    PERMISSION_DRIVE_UPLOAD,
    PERMISSION_FFDA_DASHBOARD,
    PERMISSION_FFDA_SUPPLIES,
    PERMISSION_FFDA_REPORTS,
    default as PM,
} from '../helpers/PermissionManager';
import { PageTracking } from '../helpers/UserAnalytic';
import { SupportBadge } from '../components/SupportBadge/SupportBadge';

const APP_ROUTES = [
    // PUBLIC ROUTES
    {
        isAllowed: () => true,
        routes: [<Route path="/login" element={<Pages.Auth.Login />} />],
    },
    {
        isAllowed: (session, profile) =>
            PM.isNotAuthenticated(session, profile),
        routes: [
            <Route path="*" element={<Navigate to="/login" />} />,
            <Route
                path="/forgot-password"
                element={<Pages.Auth.ForgotPassword />}
            />,
            <Route
                path="/_/catch/redirection"
                element={<Pages.System.CatchRedirection />}
            />,
        ],
    },
    // PROTECTED ROUTES
    {
        isAllowed: (session, profile) => PM.hasVirtualAccount(session, profile),
        routes: [
            // HOME REDIRECTION
            <Route path="/" element={<Navigate to="/homepage" />} />,
            // SETTINGS
            <Route path="/settings" element={<Pages.Settings.Home />} />,
            <Route
                path="/settings/profile"
                element={<Pages.Settings.Profile />}
            />,
            <Route
                path="/settings/security"
                element={<Pages.Settings.Security />}
            />,
            // SYSTEM
            <Route path="/_/leave" element={<Pages.System.SignOut />} />,
        ],
    },
    {
        isAllowed: (session, profile) => PM.isNotFederated(session, profile),
        routes: [
            <Route
                path="/documents/explorer"
                element={<Pages.Drive.Homepage />}
            />,
            <Route
                path="/documents/search"
                element={<Pages.Drive.FileSearch />}
            />,
            <Route
                path="/documents"
                element={<Navigate to="/documents/explorer" />}
            />,
        ],
    },
    {
        isAllowed: (session, profile) => PM.isNotFederated(session, profile),
        routes: [
            <Route
                path="/entity/explorer"
                element={<Pages.Documents.HomeExplorer />}
            />,
        ],
    },
    {
        isAllowed: (session, profile) =>
            // PM.isNotFederated(session, profile) &&
            PM.hasPermissions(profile, [PERMISSION_DRIVE_UPLOAD]),
        routes: [
            <Route
                path="/documents/upload-center"
                element={<Pages.Drive.UploadCenter />}
            />,
        ],
    },
    {
        isAllowed: (session, profile) =>
            PM.hasNoVirtualAccount(session, profile),
        routes: [
            <Route
                path="/portals/explore"
                element={<Pages.Portals.Explore />}
            />,
            // force redirect to portals explore
            <Route path="*" element={<Navigate to="/portals/explore" />} />,
        ],
    },
    // FEDERATED ROUTES
    {
        isAllowed: (session, profile) => PM.isFederated(session, profile),
        routes: [
            <Route
                path="/homepage"
                element={<Pages.FFDA.Homepage.Homepage />}
            />,
        ],
    },
    {
        isAllowed: (session, profile) =>
            PM.isFederated(session, profile) &&
            PM.hasPermissions(profile, [PERMISSION_FFDA_DASHBOARD]),
        routes: [
            <Route
                index
                path="/ffda/dashboard"
                element={<Pages.FFDA.Dashboard.Dashboard />}
            />,
        ],
    },
    {
        isAllowed: (session, profile) =>
            PM.isFederated(session, profile) &&
            PM.hasPermissions(profile, [PERMISSION_FFDA_REPORTS]),
        routes: [
            <Route
                path="/ffda/reports"
                element={<Pages.FFDA.Reports.Menu />}
            />,
            //<Route path="/ffda/reports/sales-activity" element={<Pages.FFDA.Reports.Sales />} />,
            <Route
                path="/ffda/reports/exports"
                element={<Pages.FFDA.Reports.FFDAReports />}
            />,
        ],
    },
    {
        isAllowed: (session, profile) =>
            PM.isFederated(session, profile) &&
            PM.hasPermissions(profile, [PERMISSION_FFDA_SUPPLIES]),
        routes: [
            <Route
                path="/ffda/supplies"
                element={<Pages.FFDA.Supplies.Supplies />}
            />,
        ],
    },
    // NOT FEDERATED ROUTES
    {
        isAllowed: (session, profile) => PM.isNotFederated(session, profile),
        routes: [<Route path="/homepage" element={<Pages.Homepage />} />],
    },
    // NOT FEDERATED & BUSINESS OWNER ROUTES
    {
        isAllowed: (session, profile) =>
            PM.isNotFederatedBusinessOwner(session, profile),
        routes: [
            //<Route path="/settings/billing" element={<Pages.Billing.Billing />} />,
        ],
    },
    // BUSINESS OWNER ROUTES
    {
        isAllowed: (session, profile) => PM.isBusinessOwner(session, profile),
        routes: [
            <Route
                path="/settings/access-management"
                element={<Pages.Settings.AccessManagement />}
            />,
            <Route
                path="/settings/access-management/user/:userId/*"
                element={<Pages.Settings.AccessManagementUser />}
            />,
            <Route
                path="/settings/access-management/invite"
                element={<Pages.Settings.InviteUser />}
            />,
        ],
    },
    // ERROR ROUTES
    {
        isAllowed: () => true,
        routes: [<Route path="*" element={<Pages.System.NotFound />} />],
    },
];

export const HasProfile = (session, profile) => {
    return profile !== null && profile !== undefined;
};

export const AppRoutes = ({ session, profile }) => {
    const routes = APP_ROUTES.reduce((routes, curr) => {
        if (curr.isAllowed(session, profile)) {
            routes = [...routes, ...curr.routes];
        }
        return routes;
    }, []);
    return (
        <Fragment>
            <PageTracking />
            <Routes>{[routes]}</Routes>
            {HasProfile(session, profile) && <SupportBadge />}
        </Fragment>
    );
};

export default AppRoutes;
